import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield } from 'lucide-react';
import { trackFunnelStep } from '@/utils/analytics';

interface InitialFormData {
  name: string;
  age: string;
  state: string;
  coverageAmount: string;
}

interface QuickInfoStepProps {
  onSubmit: (data: InitialFormData) => void;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const COVERAGE_AMOUNTS = [
  '$25,000', '$50,000', '$100,000', '$250,000', '$500,000', 
  '$750,000', '$1,000,000', '$1,500,000', '$2,000,000+'
];

export const QuickInfoStep: React.FC<QuickInfoStepProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<InitialFormData>({
    name: '',
    age: '',
    state: '',
    coverageAmount: ''
  });

  React.useEffect(() => {
    trackFunnelStep('started_form');
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackFunnelStep('completed_form');
    onSubmit(formData);
  };

  const isFormValid = formData.name && formData.age && formData.state && formData.coverageAmount;

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Get Your Life Insurance Quote
          </h1>
          <p className="text-xl text-muted-foreground">
            Let's start with some basic information about you
          </p>
        </div>
        
        <Card className="shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-4 bg-primary/10 p-3 rounded-full w-fit">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground mb-2">
              Quick Information
            </CardTitle>
            <CardDescription className="text-muted-foreground text-lg">
              Just 4 quick questions to get started
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="age" className="text-sm font-medium">Age</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="Your age"
                    min="18"
                    max="80"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="transition-all focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-sm font-medium">State of Residence</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                    <SelectTrigger className="transition-all focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coverage" className="text-sm font-medium">Desired Coverage Amount</Label>
                  <Select value={formData.coverageAmount} onValueChange={(value) => setFormData({ ...formData, coverageAmount: value })}>
                    <SelectTrigger className="transition-all focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select coverage amount" />
                    </SelectTrigger>
                    <SelectContent>
                      {COVERAGE_AMOUNTS.map((amount) => (
                        <SelectItem key={amount} value={amount}>
                          {amount}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <a 
                    href="/calculator" 
                    className="story-link text-muted-foreground hover:text-foreground transition-colors inline-block mt-2"
                  >
                    Not sure how much you need? Use our coverage calculator
                  </a>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full mt-8" 
                variant="hero"
                size="lg"
                disabled={!isFormValid}
              >
                Start Quote →
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};